import { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { apiGetAllCoupons, apiDeleteCoupon } from '../api';
import { CouponModal } from './coupons/CouponModal';
import { confirmAdminAction } from '../utils/adminConfirm';
import { getCouponTickerMessage } from '../utils/couponTicker';

const CouponsManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const data = await apiGetAllCoupons();
      setCoupons(data.coupons || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = await confirmAdminAction('This coupon will no longer be available.', { title: 'Delete coupon', confirmLabel: 'Delete' });
    if (!confirmed) return;
    try {
      await apiDeleteCoupon(id);
      setCoupons(coupons.filter((c) => c.id !== id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditing(null);
    load();
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading coupons...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-black">{coupons.length} coupons</p>
        <button
          onClick={() => {
            setEditing(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
        >
          <FiPlus size={16} /> Add Coupon
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-black">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Ticker Message</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Value</th>
                <th className="px-4 py-3 text-left">Min Order</th>
                <th className="px-4 py-3 text-left">Max Discount</th>
                <th className="px-4 py-3 text-left">Usage</th>
                <th className="px-4 py-3 text-left">Expires</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium font-mono text-orange-700">{coupon.code}</td>
                  <td className="max-w-xs px-4 py-3 text-xs text-gray-700">
                    <span className="line-clamp-2">{getCouponTickerMessage(coupon)}</span>
                    <span className="mt-1 block text-[10px] font-semibold uppercase text-gray-400">
                      {coupon.tickerMessage ? 'Custom' : 'Automatic'}
                    </span>
                  </td>
                  <td className="px-4 py-3 capitalize">{coupon.type}</td>
                  <td className="px-4 py-3 font-medium">
                    {coupon.type === 'percent' ? `${coupon.value}%` : `₹${coupon.value}`}
                  </td>
                  <td className="px-4 py-3">₹{coupon.minOrder || 0}</td>
                  <td className="px-4 py-3">
                    {coupon.maxDiscount ? `₹${coupon.maxDiscount}` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {coupon.usedCount || 0}
                    {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ' / ∞'}
                  </td>
                  <td className="px-4 py-3 text-black text-xs">
                    {coupon.expiresAt
                      ? new Date(coupon.expiresAt).toLocaleDateString()
                      : 'No expiry'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${coupon.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-black'
                        }`}
                    >
                      {coupon.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => {
                        setEditing(coupon);
                        setShowModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(coupon.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-black">
                    No coupons found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <CouponModal
          coupon={editing}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default CouponsManager;
